import React from "react";
import {
  LEGAL_CONTACT_EMAIL,
  LEGAL_EFFECTIVE_DATE,
  PRIVACY_VERSION,
  SECURITY_VERSION,
  SUBPROCESSORS_VERSION,
  TERMS_VERSION,
} from "../legal/legalVersions";
import styles from "./LegalPages.module.css";

const MailLink = () => (
  <a href={`mailto:${LEGAL_CONTACT_EMAIL}`}>{LEGAL_CONTACT_EMAIL}</a>
);

function LegalShell({
  title,
  subtitle,
  version,
  children,
}: {
  title: string;
  subtitle: string;
  version: string;
  children: React.ReactNode;
}) {
  return (
    <main className={`main-articles ${styles["legal-page"]}`}>
      <div className={styles["legal-hero"]}>
        <h1 className={styles["legal-title"]}>{title}</h1>
        <p className={styles["legal-subtitle"]}>{subtitle}</p>
        <div className={styles["legal-meta"]}>
          <span className={styles["legal-pill"]}>Effective {LEGAL_EFFECTIVE_DATE}</span>
          <span className={styles["legal-pill"]}>Version {version}</span>
          <span className={styles["legal-pill"]}>Land Agility, LLC</span>
        </div>
      </div>
      <article className={styles["legal-document"]}>{children}</article>
    </main>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className={styles["legal-section"]}>
      <h2 className={styles["legal-section-title"]}>{title}</h2>
      <div className={styles["legal-section-body"]}>{children}</div>
    </section>
  );
}

export function TermsPage() {
  return (
    <LegalShell
      title="Land Agility SaaS Terms of Service"
      subtitle="Application terms for Land Agility workspaces, subscriptions, and organization accounts."
      version={TERMS_VERSION}
    >
      <Section title="Acceptance and scope">
        <p>
          These Terms of Service are an agreement between Land Agility, LLC, a
          Texas limited liability company, and the organization or person using
          the Land Agility application. By creating a workspace, accepting an
          invitation, or using the application, you agree to these terms on
          behalf of yourself and, when applicable, your organization.
        </p>
        <p>
          These terms apply to the Land Agility application, subscriptions,
          organization accounts, and related support. Public marketing website
          use may be governed by separate website terms.
        </p>
      </Section>

      <Section title="Accounts, tenants, and users">
        <p>
          Customers are responsible for the users they invite, the roles they
          assign, and the data entered into their tenant. Owners and admins
          should grant access only to authorized personnel and promptly remove
          access when it is no longer needed.
        </p>
        <p>
          Users must provide accurate account information, keep their sign-in
          credentials secure, and use Land Agility only for lawful business
          purposes.
        </p>
      </Section>

      <Section title="Subscriptions, billing, and changes">
        <p>
          Paid plans, pricing, seat quantities, billing periods, taxes, and
          payment status are presented in the application checkout or billing
          experience unless a written agreement says otherwise. Land Agility may
          update features, plans, and prices prospectively.
        </p>
        <p>
          Subscription cancellation, downgrade, refund, and payment-failure
          handling will follow the settings shown in the application billing
          experience or an applicable written agreement.
        </p>
      </Section>

      <Section title="Land Agility technology and intellectual property">
        <p>
          Land Agility and its licensors own the application, software, designs,
          workflows, templates, documentation, branding, and other service
          technology. These terms do not transfer ownership of Land Agility
          technology to customers or users. Customers receive only the limited
          right to access and use the service for their internal business
          purposes while their account is active and in good standing.
        </p>
      </Section>

      <Section title="Customer data and acceptable use">
        <p>
          Customer data remains the customer&apos;s data. Land Agility uses
          customer data to provide, secure, support, improve, and bill for the
          service, as described in the Privacy Notice.
        </p>
        <ul>
          <li>Do not use the service in violation of law or third-party rights.</li>
          <li>Do not bypass security controls, rate limits, or access restrictions.</li>
          <li>Do not upload malicious code or attempt to disrupt the service.</li>
          <li>Do not use another tenant&apos;s data or account without authorization.</li>
        </ul>
      </Section>

      <Section title="Term, suspension, and termination">
        <p>
          Customers may stop using the service or cancel their subscription
          through the application billing experience or another method Land
          Agility makes available. Land Agility may suspend or terminate access
          if an account is unpaid, violates these terms, creates security or
          legal risk, or uses the service in a way that may harm Land Agility,
          other customers, or the service.
        </p>
        <p>
          After termination, Land Agility may retain customer data for a
          reasonable period to support account transition, legal obligations,
          backups, dispute resolution, security, and business records. Customers
          should export needed data before closing an account. Specific export,
          retention, or deletion commitments may be addressed in the application,
          a written agreement, or a support-confirmed request.
        </p>
      </Section>

      <Section title="Availability, disclaimers, and liability">
        <p>
          The service is provided on an &quot;as is&quot; and &quot;as
          available&quot; basis except as expressly stated in a written
          agreement. To the extent permitted by law, Land Agility disclaims
          implied warranties and will not be liable for indirect, incidental,
          special, consequential, exemplary, or punitive damages.
        </p>
        <p>
          To the extent permitted by law, Land Agility&apos;s total aggregate
          liability arising out of or relating to the service will not exceed the
          amounts paid to Land Agility for the service in the 12 months before
          the event giving rise to the claim.
        </p>
      </Section>

      <Section title="Governing law, venue, and standard terms">
        <p>
          These terms are governed by Texas law, without regard to
          conflict-of-law principles. To the extent permitted by law, disputes
          arising from these terms or the service must be brought in a state or
          federal court of competent jurisdiction located in Texas, and each
          party consents to the jurisdiction of those courts.
        </p>
        <p>
          If part of these terms is unenforceable, the remaining terms will
          remain in effect. Neither party is responsible for delay or failure
          caused by events beyond its reasonable control. Customers may not
          assign these terms without Land Agility&apos;s consent, except in
          connection with a merger, acquisition, or sale of substantially all
          assets. These terms, together with any applicable order, subscription,
          or written agreement, are the entire agreement for use of the service.
        </p>
      </Section>

      <Section title="Contact">
        <p>
          Questions about these terms may be sent to <MailLink />.
        </p>
      </Section>
    </LegalShell>
  );
}

export function PrivacyPage() {
  return (
    <LegalShell
      title="Land Agility Application Privacy Notice"
      subtitle="How Land Agility handles information in application workspaces and account workflows."
      version={PRIVACY_VERSION}
    >
      <Section title="Information we process">
        <p>
          Land Agility processes information needed to operate business
          workspaces, including account profile details, tenant/company profile
          details, invited users, clients, prospects, time entries, expenses,
          approvals, reports, invoices, billing metadata, audit events, support
          communications, and uploaded company logos.
        </p>
      </Section>

      <Section title="How we use information">
        <ul>
          <li>Operate, secure, support, and improve the application.</li>
          <li>Authenticate users and enforce tenant, role, feature, and subscription controls.</li>
          <li>Generate reports, invoices, PDF exports, and Excel exports requested by users.</li>
          <li>Process subscription, billing, payment-status, and account administration workflows.</li>
          <li>Investigate errors, prevent abuse, and comply with legal obligations.</li>
        </ul>
      </Section>

      <Section title="Service providers and disclosures">
        <p>
          Land Agility uses service providers to host infrastructure, authenticate
          users, process payments, run deployments, and operate supporting
          business systems. The current public subprocessor list is available at{" "}
          <a href="/subprocessors">subprocessors</a>.
        </p>
        <p>
          Land Agility does not sell customer data or share it for cross-context
          behavioral advertising. Information may be disclosed when required by
          law, to protect the service and legal rights, or as part of a business
          transaction.
        </p>
        <p>
          Application customer data is stored in Land Agility&apos;s Azure-hosted
          application infrastructure, including database storage. Land Agility
          does not send application customer data to separate vendors for
          analytics, artificial intelligence training, or other additional data
          processing.
        </p>
      </Section>

      <Section title="Customer data, roles, and machine learning">
        <p>
          Land Agility generally acts as a service provider or processor for
          tenant-entered customer data, such as client, prospect, time, expense,
          approval, report, and invoice records. Land Agility acts as an
          independent business or controller for account administration, billing,
          security, support, and legal compliance information.
        </p>
        <p>
          Land Agility does not use one customer&apos;s data to benefit another
          customer, and Land Agility does not use customer data to train
          machine-learning models. Customers that need specific data-processing
          terms may contact <MailLink />.
        </p>
      </Section>

      <Section title="Retention, deletion, and requests">
        <p>
          Customer data is retained while needed to provide the service, maintain
          business records, comply with legal obligations, resolve disputes, and
          protect the service. Customers may contact Land Agility about access,
          correction, export, deletion, or tenant closure requests at{" "}
          <MailLink />.
        </p>
      </Section>

      <Section title="Security and international processing">
        <p>
          Land Agility uses administrative and technical safeguards intended to
          protect application information. No online service can guarantee
          absolute security. Service providers may process information in
          locations where they operate, subject to their service arrangements and
          applicable law.
        </p>
      </Section>

      <Section title="Contact">
        <p>
          Privacy questions and requests may be sent to <MailLink />.
        </p>
      </Section>
    </LegalShell>
  );
}

export function SubprocessorsPage() {
  const providers = [
    ["Microsoft Azure", "Cloud hosting, application infrastructure, Azure SQL database storage, and identity services"],
    ["Stripe", "Subscription checkout, payment processing, invoices, and billing workflows"],
    ["GitHub", "Source control, CI/CD workflows, and deployment automation"],
  ];

  return (
    <LegalShell
      title="Land Agility Subprocessors"
      subtitle="Service providers that may support application delivery and business operations."
      version={SUBPROCESSORS_VERSION}
    >
      <Section title="Current subprocessors">
        <div className={styles["legal-table-wrap"]}>
          <table className={styles["legal-table"]}>
            <thead>
              <tr>
                <th>Provider</th>
                <th>Purpose</th>
              </tr>
            </thead>
            <tbody>
              {providers.map(([provider, purpose]) => (
                <tr key={provider}>
                  <td>{provider}</td>
                  <td>{purpose}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>
      <Section title="Third-party assets">
        <p>
          The application shell may load public icon/font assets from Font
          Awesome CDN. This is listed separately from application subprocessors
          because it supports public asset delivery rather than core customer
          data processing.
        </p>
      </Section>
      <Section title="Updates and questions">
        <p>
          This list may be updated as Land Agility adds or changes material
          service providers. Questions may be sent to <MailLink />.
        </p>
      </Section>
    </LegalShell>
  );
}

export function SecurityPage() {
  return (
    <LegalShell
      title="Land Agility Security"
      subtitle="A concise, fact-based summary of current application security practices."
      version={SECURITY_VERSION}
    >
      <Section title="Current verified controls">
        <ul>
          <li>Application sign-in uses Azure AD External ID with MSAL.</li>
          <li>API calls include authenticated user and active tenant context headers.</li>
          <li>The frontend implements role, tenant, feature, and tier guards.</li>
          <li>The PDF service uses short-lived UUID tokens for report payloads.</li>
          <li>The PDF service applies Helmet security headers, configured CORS, and PDF endpoint rate limiting.</li>
          <li>GitHub Actions CI runs architecture checks, frontend build/tests, and server tests.</li>
          <li>Azure deployment uses GitHub Actions OIDC to deploy to Azure App Service.</li>
        </ul>
      </Section>

      <Section title="Scope notes">
        <p>
          Backend data persistence, Azure resource configuration, identity
          provider policy details, monitoring, backups, and compliance
          attestations are operational controls that are not currently described
          in detail on this public page. Customers may contact Land Agility for
          current details.
        </p>
      </Section>

      <Section title="Report a concern">
        <p>
          Security questions or suspected vulnerabilities may be sent to{" "}
          <MailLink />.
        </p>
      </Section>
    </LegalShell>
  );
}
